import React from 'react';
import { Pressable } from 'react-native';

import ArrowRightIcon from '@/assets/images/level-detail/skill-detail/arrow-right.png';
import StarIcon from '@/assets/images/level-detail/skill-detail/star.png';
import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type {
  GuardianPlayerLevelDetailAttempt,
  GuardianPlayerLevelDetailPin,
} from '@/features/guardian-player/types';
import { CHECKPOINT_LEADER_DOTS } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';
import { LevelDetailInfoCard, useInfoCardStyles } from './level-detail-info-card';
import { LevelDetailPinBadge } from './pin-badge';

type LevelDetailPanelProps = {
  pin: GuardianPlayerLevelDetailPin;
  standalone?: boolean;
  insideScroll?: boolean;
  noDivider?: boolean;
};

type AttemptGroup = {
  id: string;
  label: string;
  statusLabel: string;
  completed: boolean;
  incompleteLabels: string[];
};

function buildAttemptGroups(attempts: GuardianPlayerLevelDetailAttempt[]): AttemptGroup[] {
  const copy = STRINGS.guardianPlayer.levelDetail.panel;
  const progressCopy = STRINGS.player.profile.progressTrackerContent;
  const groups: AttemptGroup[] = [];

  attempts.forEach(attempt => {
    const shouldStartGroup = attempt.groupStart || groups.length === 0;
    let currentGroup = groups[groups.length - 1];

    if (shouldStartGroup) {
      currentGroup = {
        id: `${attempt.id}-group`,
        label: attempt.label,
        statusLabel: progressCopy.statusNotComplete,
        completed: false,
        incompleteLabels: [],
      };
      groups.push(currentGroup);
    }

    if (attempt.completed) {
      currentGroup.statusLabel = attempt.completedOn
        ? copy.completedOn(attempt.completedOn)
        : progressCopy.completed;
      currentGroup.completed = true;
      return;
    }

    if (attempt.incompleteOn) {
      const incompleteLabel = copy.incompleteAttemptOn(attempt.incompleteOn);
      if (!currentGroup.incompleteLabels.includes(incompleteLabel)) {
        currentGroup.incompleteLabels.push(incompleteLabel);
      }
    }
  });

  return groups.map((group, index) => ({
    ...group,
    id: `${group.id}-${index}`,
  }));
}

export function LevelDetailPanel({
  pin,
  standalone = false,
  insideScroll = false,
  noDivider = false,
}: LevelDetailPanelProps) {
  const navigation = useGuardianStackNavigation();
  const styles = useStyles();
  const infoStyles = useInfoCardStyles();
  const { scale, isTablet } = useScaling();
  const hasWhatItTests = Boolean(pin.whatItTests?.trim());
  const criteria = pin.criteria ?? [];
  const showSkillActions = pin.status !== 'notStarted';
  const attemptGroups = React.useMemo(() => buildAttemptGroups(pin.attempts), [pin.attempts]);
  const isMobileStandalone = standalone && !isTablet;
  const statusPillStyle = [
    styles.statusPill,
    isMobileStandalone ? styles.mobileStatusPill : null,
    pin.status === 'earned' ? styles.earnedStatusPill : null,
    pin.status === 'working' ? styles.workingStatusPill : null,
    pin.status === 'notStarted' ? styles.notStartedStatusPill : null,
  ];
  const statusTextStyle = [
    styles.statusText,
    pin.status === 'earned' ? styles.earnedStatusText : null,
    pin.status === 'working' ? styles.workingStatusText : null,
    pin.status === 'notStarted' ? styles.notStartedStatusText : null,
  ];

  return (
    <Stack
      flex={standalone || insideScroll ? undefined : 1}
      style={[styles.panel, standalone || noDivider ? styles.standalonePanel : null]}>
      <Stack
        direction={isMobileStandalone ? 'column' : 'row'}
        align="center"
        gap={isMobileStandalone ? scale(12) : scale(40)}
        style={[styles.hero, isMobileStandalone ? styles.mobileHero : null]}>
        <LevelDetailPinBadge
          pin={pin}
          size={scale(100)}
          showCompletedBadge={pin.status === 'earned'}
        />
        <Stack
          gap={isMobileStandalone ? scale(10) : scale(14)}
          flex={isMobileStandalone ? undefined : 1}
          minWidth={0}
          align={isMobileStandalone ? 'center' : 'stretch'}>
          <Stack
            direction="row"
            align="center"
            justify={isMobileStandalone ? 'center' : 'space-between'}
            gap={scale(12)}>
            <Text
              fontFamily={fontFamilies.montserrat}
              fontSize={isMobileStandalone ? scale(24) : scale(20)}
              lineHeight={isMobileStandalone ? scale(32) : scale(28)}
              fontWeight="700"
              style={[styles.heroTitle, isMobileStandalone ? styles.mobileHeroTitle : null]}>
              {pin.title}
            </Text>
          </Stack>
          <Stack direction="row" align="center" gap={scale(6)} style={statusPillStyle}>
            <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={statusTextStyle}>
              {STRINGS.guardianPlayer.levelDetail.pinStatus[pin.status]}
            </Text>
            {pin.status === 'earned' && pin.date ? (
              <>
                <Image
                  source={StarIcon}
                  width={scale(10)}
                  height={scale(10)}
                  resizeMode="contain"
                />
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
                  style={statusTextStyle}>
                  {pin.date}
                </Text>
              </>
            ) : null}
          </Stack>
        </Stack>
      </Stack>

      <Stack gap={scale(8)} style={styles.body}>
        {hasWhatItTests ? (
          <LevelDetailInfoCard
            iconSource={'IcTarget'}
            title={STRINGS.guardianPlayer.levelDetail.panel.whatItTests}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={infoStyles.bodyText}>
              {pin.whatItTests}
            </Text>
          </LevelDetailInfoCard>
        ) : null}

        {criteria.length > 0 ? (
          <LevelDetailInfoCard
            iconSource={'IcCheckListMd'}
            title={STRINGS.guardianPlayer.levelDetail.panel.criteria}>
            <Stack gap={scale(10)}>
              {criteria.map(item => (
                <Stack key={item} style={infoStyles.criteriaRow}>
                  <SvgIcon name="IcCheckMd" size={scale(16)} color={guardianColors['10']} />
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                    style={infoStyles.bodyText}>
                    {item}
                  </Text>
                </Stack>
              ))}
            </Stack>
          </LevelDetailInfoCard>
        ) : null}

        {showSkillActions ? (
          <LevelDetailInfoCard
            iconSource={'IcAttempts'}
            title={STRINGS.guardianPlayer.levelDetail.panel.attempts}>
            {attemptGroups.length === 0 ? (
              <Text variant="Font Inter/Subtitle/Regular" style={styles.emptyAttempts}>
                {STRINGS.guardianPlayer.levelDetail.panel.noAttempts}
              </Text>
            ) : (
              <Stack>
                {attemptGroups.map((group, index) => (
                  <Stack key={group.id}>
                    {index > 0 ? <Stack style={styles.attemptDivider} /> : null}
                    <Stack gap={scale(2)}>
                      <Stack direction="row" align="center" style={styles.attemptHeaderRow}>
                        <Text
                          variant="Font Inter/Subtitle/Regular"
                          style={styles.attemptHeaderLabel}
                          numberOfLines={1}>
                          {group.label}
                        </Text>
                        <Text
                          numberOfLines={1}
                          ellipsizeMode="clip"
                          variant="Font Inter/Subtitle/Regular"
                          style={styles.attemptLeaderText}>
                          {CHECKPOINT_LEADER_DOTS}
                        </Text>
                        <Stack style={styles.attemptStatusMask}>
                          <Text
                            variant="Font Inter/Subtitle/Regular"
                            numberOfLines={1}
                            style={[
                              styles.attemptHeaderStatus,
                              group.completed ? styles.attemptCompleted : null,
                            ]}>
                            {group.statusLabel}
                          </Text>
                        </Stack>
                      </Stack>

                      {group.incompleteLabels.length > 0 ? (
                        <Stack align="flex-end" gap={scale(2)}>
                          {group.incompleteLabels.map((label, incompleteIndex) => (
                            <Text
                              key={`${group.id}-incomplete-${incompleteIndex}`}
                              variant="Font Inter/Subtitle/Regular"
                              fontStyle="italic"
                              style={styles.attemptIncompleteText}>
                              {label}
                            </Text>
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </LevelDetailInfoCard>
        ) : null}

        <Pressable
          accessibilityRole="button"
          onPress={() => navigation.navigate('GuardianCoachMaterials')}
          style={({ pressed }) => [styles.materialButton, pressed ? styles.pressed : null]}>
          <Text
            variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
            style={styles.materialText}>
            {STRINGS.guardianPlayer.levelDetail.panel.viewCoachMaterial}
          </Text>
          <Image
            source={ArrowRightIcon}
            width={scale(24)}
            height={scale(24)}
            resizeMode="contain"
          />
        </Pressable>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  panel: {
    borderLeftWidth: 1,
    borderLeftColor: guardianColors['3'],
  },
  standalonePanel: {
    borderLeftWidth: 0,
  },
  hero: {
    paddingTop: scale(20),
    paddingHorizontal: scale(20),
    paddingBottom: scale(18),
  },
  mobileHero: {
    paddingTop: scale(4),
    paddingBottom: scale(20),
  },
  heroTitle: {
    color: guardianColors['10'],
    flex: 1,
  },
  mobileHeroTitle: {
    flex: undefined,
    textAlign: 'center',
  },
  statusPill: {
    alignSelf: 'flex-start',
    minHeight: scale(30),
    paddingHorizontal: scale(16),
    borderRadius: scale(30),
  },
  mobileStatusPill: {
    alignSelf: 'center',
  },
  earnedStatusPill: {
    backgroundColor: guardianColors['Main/10'],
    shadowColor: colors.Blue,
    shadowOpacity: 0.16,
    shadowOffset: { width: 0, height: scale(2) },
    shadowRadius: scale(2),
    elevation: 1,
  },
  workingStatusPill: {
    backgroundColor: '#FFEED9',
  },
  notStartedStatusPill: {
    backgroundColor: guardianColors['3'],
  },
  statusText: {
    textAlign: 'center',
  },
  earnedStatusText: {
    color: colors.Blue,
  },
  workingStatusText: {
    color: guardianColors['Primary Color'],
  },
  notStartedStatusText: {
    color: guardianColors['6'],
  },
  body: {
    paddingHorizontal: scale(19),
  },
  emptyAttempts: {
    color: guardianColors['6'],
  },
  attemptDivider: {
    height: 1,
    backgroundColor: guardianColors['3'],
    marginVertical: scale(6),
  },
  attemptHeaderRow: {
    width: '100%',
    minHeight: scale(16),
  },
  attemptHeaderLabel: {
    color: guardianColors['6'],
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    maxWidth: scale(170),
    backgroundColor: colors['0'],
    paddingRight: scale(6),
    zIndex: 9,
  },
  attemptLeaderText: {
    flex: 1,
    marginHorizontal: scale(8),
    borderBottomColor: guardianColors['6'],
    color: guardianColors['6'],
    opacity: 0.65,
  },
  attemptStatusMask: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors['0'],
    paddingLeft: scale(6),
    justifyContent: 'center',
    zIndex: 9,
  },
  attemptHeaderStatus: {
    color: guardianColors['6'],
    textAlign: 'right',
  },
  attemptCompleted: {
    color: colors.Success,
  },
  attemptIncompleteText: {
    color: guardianColors['6'],
    textAlign: 'right',
  },
  materialButton: {
    height: scale(32),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: scale(10),
    paddingHorizontal: scale(10),
  },
  materialText: {
    color: colors.Blue,
  },
  pressed: {
    opacity: 0.72,
  },
}));
